import { createFileRoute, Link } from "@tanstack/react-router";
import { Sparkles, ShieldCheck, Wallet, Recycle, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/lib/i18n/locale-context";
import { getPageCopy } from "@/lib/i18n/page-copy";
import { localizedPath } from "@/lib/i18n/slug-map";

export const Route = createFileRoute("/varfor-kostar-det")({
  head: () => ({
    meta: [
      { title: "Varför kostar det att annonsera? | Sellavo" },
      {
        name: "description",
        content:
          "Varför kostar det att lägga ut annonser på Sellavo? Vi granskar bilderna, skriver annonstexten och föreslår pris – här förklarar vi kostnaden bakom gratis- och betalkonton.",
      },
      { property: "og:title", content: "Varför kostar det att annonsera? | Sellavo" },

      {
        property: "og:description",
        content:
          "AI gör annonseringen snabb och enkel. Läs varför fler annonser än gratisnivån kostar en liten slant.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://sellavo.com/varfor-kostar-det" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://sellavo.com/varfor-kostar-det" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Varför kostar det att annonsera på Sellavo?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "AI granskar bilderna, skriver annonstexten och föreslår pris. Det kostar pengar varje gång – därför är gratisnivån begränsad medan Plus och Pro täcker mer AI-användning.",
              },
            },
            {
              "@type": "Question",
              name: "Är det gratis att lägga ut en annons?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Ja. Gratiskontot låter dig publicera annonser varje månad. Vill du ha fler annonser, fler bilder eller video väljer du Plus eller Pro.",
              },
            },
            {
              "@type": "Question",
              name: "Finns det några dolda avgifter?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Nej. Priserna visas inklusive moms och vi tar ingen provision på det du säljer.",
              },
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Start", item: "/" },
            {
              "@type": "ListItem",
              position: 2,
              name: "Varför kostar det?",
              item: "/varfor-kostar-det",
            },
          ],
        }),
      },
    ],
  }),
  component: WhyItCosts,
});

export function WhyItCosts() {
  const { lang } = useLocale();
  const copy = lang === "sv" ? null : getPageCopy(lang).whyCost;
  const icons = [Sparkles, ShieldCheck, Wallet, Recycle];
  const svCards = [
    ["AI skriver och granskar åt dig", "När du fotar en pryl läser vår AI av bilden, skriver en säljande titel och beskrivning, föreslår ett rimligt pris och placerar annonsen i rätt kategori. Det är det som gör att du kan gå från foto till publicerad annons på under en minut."],
    ["Kvalitet och trygghet kostar", "Vi granskar även innehållet så att uppenbart olämpliga annonser stoppas innan de når sajten. Vi vill att både säljare och köpare ska känna sig trygga – det kräver löpande övervakning, uppdateringar och en pålitlig teknikplattform."],
    ["Fler annonser = mer resursåtgång", "Det är därför du får lägga upp ett litet antal annonser helt gratis varje månad. Plus- eller Pro-abonnemanget täcker de extra kostnaderna för bildbehandling, textgenerering och serverdrift."],
    ["Inga dolda avgifter", "Det kostar inget att skapa ett konto, titta på annonser eller skicka meddelanden. Du betalar bara om du vill lägga upp fler annonser än vad gratisnivån rymmer."],
  ];
  const cards = copy?.cards ?? svCards;
  return (
    <main className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <h1 className="font-heading text-3xl font-extrabold leading-tight tracking-tight text-foreground sm:text-4xl">
        {copy?.title ?? "Varför kostar det på Sellavo?"}
      </h1>
      <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
        {copy?.intro ?? "Kort svar: för att vi använder artificiell intelligens för att göra hela försäljningsflödet snabbare, snyggare och säkrare för dig. Ju fler annonser som skapas, desto mer AI-kapacitet behöver vi köpa in."}
      </p>

      <div className="mt-10 space-y-4">
        {cards.map(([title, body], index) => {
          const Icon = icons[index];
          return <div key={title} className="rounded-lg border border-border bg-card p-5 sm:p-6">
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="shrink-0 rounded-md bg-ocean-100/60 p-2.5 text-primary sm:p-3">
              <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="min-w-0">
              <h2 className="font-heading text-base sm:text-lg font-semibold text-foreground">
                {title}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
                {body}
              </p>
            </div>
          </div>
        </div>})}
      </div>

      <div className="mt-10 rounded-lg bg-ocean-900 px-6 py-8 text-ocean-100">
        <h2 className="font-heading text-xl font-semibold text-white">{copy?.thinking ?? "Så här tänker vi"}</h2>
        <p className="mt-3">
          {copy?.thinkingBody ?? "Sellavo ska vara det snabbaste sättet att bli av med en pryl. Vi tar hand om det tråkiga – text, pris och kategorisering – så att du slipper. Därför behöver vi ta betalt för den kapaciteten när den används mycket, men vi håller priserna låga så att det fortfarande lönar sig att sälja även småsaker."}
        </p>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <Button asChild variant="default" className="bg-white text-ocean-900 hover:bg-ocean-50">
            <Link to={localizedPath("/priser", lang)}>{copy?.prices ?? "Se priser och nivåer"}</Link>
          </Button>
          <Button
            asChild
            variant="outline"
            className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white"
          >
            <Link to={localizedPath("/", lang)}>{copy?.free ?? "Skapa en gratis annons"}</Link>
          </Button>
        </div>
      </div>

      <div className="mt-10">
        <h2 className="flex items-center gap-2 font-heading text-xl font-semibold text-foreground">
          <HelpCircle className="h-5 w-5 text-primary" />
          {copy?.faq ?? "Vanliga frågor"}
        </h2>
        <dl className="mt-4 space-y-4">
          {(copy?.faqs ?? [["Varför får jag inte lägga upp hur många annonser jag vill gratis?", "Varje annons kostar oss pengar i AI- och serveranvändning. Gratisnivån låter dig testa Sellavo och sälja några saker utan att betala."], ["Går pengarna till något bra?", "Ja – de går till drift, AI-modeller, bildlagring, säkerhet och löpande utveckling av sajten."], ["Kan jag avbryta när som helst?", "Ja. Du kan pausa enskilda annonser eller låta ditt konto stå kvar utan att betala."]]).map(([q, a]) => <div key={q} className="rounded-lg border border-border bg-card p-4"><dt className="font-medium text-foreground">{q}</dt><dd className="mt-2 text-sm text-muted-foreground">{a}</dd></div>)}
        </dl>
      </div>
    </main>
  );
}
